import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlanCatalogCard } from '../../modules/billing/components/PlanCatalogCard';
import {
  createSchoolCheckoutSession,
  getPublicSubscriptionPlanCatalog,
  type PublicPlanCatalogRow,
} from '../../modules/billing/api/billing.client';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { ROUTES, USER_ROLES } from '../../utils/constants';
import { showErrorToast } from '../../utils/errorHandler';

const PricingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(() =>
    searchParams.get('cycle') === 'yearly' ? 'yearly' : 'monthly'
  );
  const [plans, setPlans] = useState<PublicPlanCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);

  const schoolId = useMemo(() => {
    const fromStorage = (localStorage.getItem('X_TENANT_ID') || '').trim();
    if (fromStorage) return fromStorage;
    const m = user?.memberships?.find((x) => x.school_id);
    return m?.school_id || '';
  }, [user]);

  const isSchoolAdmin = useMemo(
    () => user?.memberships?.some((m) => m.role === USER_ROLES.SCHOOL_ADMIN) ?? false,
    [user]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await getPublicSubscriptionPlanCatalog();
        if (!cancelled) setPlans(rows.sort((a, b) => a.display_order - b.display_order));
      } catch (e) {
        if (!cancelled) showErrorToast(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscribe = useCallback(
    async (plan: PublicPlanCatalogRow) => {
      const pk = plan.plan_key.toUpperCase();
      if (pk === 'ENTERPRISE') {
        navigate(ROUTES.CONTACT);
        return;
      }
      if (!plan.stripe_checkout_enabled) {
        showErrorToast(new Error('Self-serve checkout is not available for this plan yet.'));
        return;
      }
      if (!isAuthenticated || !isSchoolAdmin || !schoolId) {
        const returnTo = `/pricing?plan=${encodeURIComponent(pk)}&cycle=${billingCycle}`;
        navigate(`${ROUTES.LOGIN}?mode=admin&returnTo=${encodeURIComponent(returnTo)}`);
        return;
      }
      if (!plan.available_cycles.includes(billingCycle)) {
        showErrorToast(new Error(`${billingCycle} billing is not available for ${pk}.`));
        return;
      }
      setCheckoutPlan(pk);
      try {
        const { checkout_url } = await createSchoolCheckoutSession({
          school_id: schoolId,
          plan_key: pk,
          billing_cycle: billingCycle,
        });
        window.location.href = checkout_url;
      } catch (e) {
        showErrorToast(e);
        setCheckoutPlan(null);
      }
    },
    [billingCycle, isAuthenticated, isSchoolAdmin, navigate, schoolId]
  );

  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (!planParam || !isAuthenticated || !isSchoolAdmin || !schoolId || loading) return;
    const match = plans.find((p) => p.plan_key.toUpperCase() === planParam.toUpperCase());
    if (match) void handleSubscribe(match);
  }, [searchParams, isAuthenticated, isSchoolAdmin, schoolId, loading, plans, handleSubscribe]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <header className="border-b border-slate-200 bg-white/80 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to={ROUTES.LANDING} className="text-lg font-bold text-primary-700 dark:text-primary-400">
            Tamil Katral Maiyam
          </Link>
          <div className="flex gap-3">
            <Link to={ROUTES.PRICING} className="text-sm text-primary-600 underline">
              Pricing
            </Link>
            <Link to={ROUTES.CONTACT} className="text-sm text-primary-600 underline">
              Contact
            </Link>
            {isAuthenticated ? (
              <Link to={ROUTES.ADMIN_BILLING} className="text-sm text-primary-600 underline">
                My billing
              </Link>
            ) : (
              <Link to={ROUTES.LOGIN} className="text-sm text-primary-600 underline">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-app-text dark:text-white">Plans for Tamil schools</h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            Choose a plan with the seats, modules, and live AI minutes your school needs.
          </p>
          <div className="mt-6 inline-flex rounded-lg border border-slate-200 p-1 dark:border-slate-700">
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                billingCycle === 'monthly'
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-600 dark:text-slate-300'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                billingCycle === 'yearly'
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-600 dark:text-slate-300'
              }`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
            </button>
          </div>
        </div>

        {loading ? (
          <p className="mt-12 text-center text-slate-500">Loading plans…</p>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => {
              const isEnterprise = plan.plan_key.toUpperCase() === 'ENTERPRISE';
              const canCheckout =
                plan.stripe_checkout_enabled &&
                !isEnterprise &&
                plan.available_cycles.includes(billingCycle);
              return (
                <PlanCatalogCard
                  key={plan.plan_key}
                  plan={plan}
                  billingCycle={billingCycle}
                  highlight={plan.plan_key.toUpperCase() === 'GROWTH'}
                  showCheckoutHint
                  action={
                    isEnterprise ? (
                      <Button variant="outline" className="w-full" onClick={() => void handleSubscribe(plan)}>
                        Contact us
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        disabled={!canCheckout || checkoutPlan === plan.plan_key}
                        onClick={() => void handleSubscribe(plan)}
                      >
                        {checkoutPlan === plan.plan_key ? 'Redirecting…' : 'Subscribe'}
                      </Button>
                    )
                  }
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PricingPage;
